import { FileText, Download, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";

const ARTICOLI = [
  { n: "1", title: "Oggetto", body: "Il presente Standard Agreement disciplina la cessione della partecipazione totalitaria nel capitale sociale dell'azienda target ('Società') dal Venditore all'Acquirente. Prezzo espresso in Enterprise Value con rettifica standard per Cassa e Debito Finanziario Netto alla data di closing." },
  { n: "2", title: "Prezzo e formula di valutazione", body: "Prezzo = EBITDA normalizzato ultimo esercizio × Multiplo di Settore (definito nell'Allegato B, versione mensilmente aggiornata dall'Osservatorio GIANO) − Debito Finanziario Netto. Il metodo è pubblico e riproducibile. Qualunque scostamento deve essere motivato e documentato." },
  { n: "3", title: "Il 'per sempre' — clausola verificabile", body: "L'Acquirente si impegna, per almeno 10 (dieci) anni dal closing, a: (a) mantenere il marchio della Società; (b) mantenere la sede operativa nel comune di riferimento; (c) non ridurre l'organico oltre il 15% cumulato per motivi diversi da eventi di forza maggiore. Ogni violazione comporta penale automatica pari al 20% del prezzo." },
  { n: "4", title: "Vendor loan standard", body: "Il 20-30% del prezzo può essere convertito in vendor loan a 7 anni, tasso 4% fisso, garanzia sulla stessa quota ceduta, rimborso annuale posticipato. Termini pre-negoziati e non discutibili una volta scelta la formula." },
  { n: "5", title: "Data room portabile", body: "Il Venditore consegna il dossier nel formato standard Passaporto del Deal (Allegato C). La due diligence dell'Acquirente si limita a verifica confermativa (max 15 giorni lavorativi), non esplorativa." },
  { n: "6", title: "Warranties e W&I insurance", body: "Il set di warranties è standardizzato (Allegato D). Il Venditore sottoscrive polizza W&I inclusa nel deal — costo circa 1-1.5% dell'Enterprise Value — che copre eventuali disclosure omesse in DD. Zero contenzioso post-closing sui capitoli coperti." },
  { n: "7", title: "Employment & key people", body: "I dipendenti chiave identificati nell'Allegato E ricevono un bonus di permanenza finanziato nel deal (max 5% del prezzo), erogato in tre tranche annuali. Il fondatore mantiene ruolo di consulenza retribuita per 24 mesi (facoltativo)." },
  { n: "8", title: "Trasparenza", body: "L'Acquirente pubblica nel Registro delle Promesse GIANO, con cadenza annuale, i valori di dipendenti, sede, marchio e investimenti realizzati sulla Società. Consenso esplicito del Venditore alla pubblicazione." },
];

export default function StandardAgreement() {
  return (
    <main data-testid="standard-page">
      <SEO
        title="GIANO Standard Agreement"
        description="Il contratto standard di cessione PMI. Open-source, gratuito, come il SAFE di Y Combinator ma per le PMI italiane senza successione. 8 articoli con termini pre-negoziati per chiudere in 90-120 giorni invece di 12-15 mesi."
        path="/standard-agreement"
      />
      <section style={{ background: "var(--bone)" }} className="py-20 md:py-28">
        <div className="container-x">
          <div className="overline" style={{ color: "var(--terracotta)" }}>GIANO Standard Agreement</div>
          <h1 className="serif-display mt-6" style={{ fontSize: "clamp(40px, 5.5vw, 76px)", lineHeight: 1.05, maxWidth: 1100 }}>
            Il contratto standard<br />
            <span style={{ color: "var(--terracotta)", fontStyle: "italic" }}>di cessione PMI.</span> Open-source. Gratis.
          </h1>
          <p className="mt-8 text-lg md:text-xl" style={{ maxWidth: 780, opacity: 0.78, lineHeight: 1.7 }}>
            Come il SAFE di Y Combinator per le startup, ma per la cessione di PMI italiane senza successione. Un contratto pubblico, prevedibile, validato da uno studio legale primario, che chiunque può usare — anche fuori da GIANO. Chi lo adotta normalizza il mercato.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <button data-testid="download-pdf" onClick={() => window.print()} className="btn-primary"><Download size={16} /> Stampa / Salva PDF</button>
            <a href="/piattaforma" className="btn-ghost">Torna alla piattaforma</a>
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20" style={{ background: "var(--muted)" }}>
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card icon={FileText} title="Pubblico" body="Il testo è scaricabile in versione integrale, senza registrazione. Chiunque può leggerlo, criticarlo, migliorarlo." />
            <Card icon={CheckCircle2} title="Prevedibile" body="Termini chiave non negoziabili: prezzo formulaico, clausola 'per sempre', vendor loan, W&I. Le trattative si concentrano sulle 3 leve residue, non su 50." />
            <Card icon={FileText} title="Adottabile fuori da GIANO" body="Non serve il nostro consenso. Se venditore e acquirente lo firmano, hanno gli stessi standard di qualità. Il nostro brand cresce col mercato." />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container-x max-w-3xl">
          <div className="mb-12">
            <div className="overline">Preambolo</div>
            <h2 className="serif-display mt-4" style={{ fontSize: "clamp(28px, 3.5vw, 40px)" }}>Cosa cambia</h2>
            <p className="mt-6 text-lg" style={{ lineHeight: 1.75 }}>
              In un deal standard italiano ci sono in media 47 punti di negoziazione tra prima bozza e signing. Molti sono irrilevanti, alcuni sono critici, tutti si trattano di nuovo ogni volta. Il Giano Standard Agreement pre-negozia 40 di quei 47 punti, lasciandone 7 sul tavolo. Risultato: il deal si chiude in 90-120 giorni invece di 12-15 mesi.
            </p>
          </div>

          <div className="space-y-10">
            {ARTICOLI.map(a => (
              <div key={a.n} data-testid={`articolo-${a.n}`}>
                <div className="flex items-baseline gap-4">
                  <div className="serif-display" style={{ fontSize: 36, color: "var(--terracotta)", lineHeight: 1 }}>{a.n}</div>
                  <h3 className="serif-display" style={{ fontSize: 26 }}>{a.title}</h3>
                </div>
                <p className="mt-4 text-lg" style={{ lineHeight: 1.75, opacity: 0.85 }}>{a.body}</p>
              </div>
            ))}
          </div>

          <div className="mt-16 p-6 border" style={{ borderColor: "var(--border)", background: "var(--card)" }}>
            <div className="font-mono text-xs" style={{ opacity: 0.6 }}>ALLEGATI (in preparazione)</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li>Allegato A — Metodologia di normalizzazione EBITDA</li>
              <li>Allegato B — Multipli di settore aggiornati mensilmente</li>
              <li>Allegato C — Format del Passaporto del Deal</li>
              <li>Allegato D — Set standard di warranties</li>
              <li>Allegato E — Template Employment & Key People</li>
            </ul>
          </div>

          <div className="mt-16 text-xs font-mono" style={{ opacity: 0.55 }}>
            Versione 0.9-beta · Luglio 2026 · In fase di validazione con studio legale primario · CC BY 4.0
          </div>
        </div>
      </section>
    </main>
  );
}
function Card({ icon: Icon, title, body }) {
  return (
    <div className="card-flat p-6 md:p-8">
      <Icon size={22} style={{ color: "var(--terracotta)" }} />
      <h3 className="serif-display mt-4" style={{ fontSize: 22 }}>{title}</h3>
      <p className="mt-3 text-sm" style={{ opacity: 0.78, lineHeight: 1.7 }}>{body}</p>
    </div>
  );
}
